import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranscriptionJobs } from "../contexts/TranscriptionContext";
import PodcastSearchBar from "../components/browse/PodcastSearchBar";
import PodcastGrid from "../components/browse/PodcastGrid";
import SubscriptionsSection from "../components/browse/SubscriptionsSection";
import LatestEpisodeCard from "../components/browse/LatestEpisodeCard";
// Skeleton import removed – Latest Episodes no longer uses skeleton loaders
import {
  searchPodcasts,
  getSubscriptions,
  getPopularPodcasts,
  getLatestEpisodes,
  getPodcastFeed,
  transcribeEpisode,
  checkTranscribed,
} from "../services/podcastApi";
import type { PodcastSearchResult, Subscription, LatestEpisode } from "../types/podcast";

export default function BrowsePage() {
  const { user } = useAuth();
  const { addJob, addCompletedJob } = useTranscriptionJobs();
  const [transcribingGuids, setTranscribingGuids] = useState<Set<string>>(new Set());

  const [searchResults, setSearchResults] = useState<PodcastSearchResult[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingSubs, setIsLoadingSubs] = useState(true);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeQuery, setActiveQuery] = useState("");
  const [popularPodcasts, setPopularPodcasts] = useState<PodcastSearchResult[]>([]);
  const [isLoadingPopular, setIsLoadingPopular] = useState(true);
  const [latestEpisodes, setLatestEpisodes] = useState<LatestEpisode[]>([]);
  const [isLoadingLatest, setIsLoadingLatest] = useState(true);
  const [latestFromFavorites, setLatestFromFavorites] = useState<LatestEpisode[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [transcribedUrls, setTranscribedUrls] = useState<Set<string>>(new Set());

  // Load subscriptions on mount (logged-in users only)
  useEffect(() => {
    if (!user) {
      setIsLoadingSubs(false);
      setSubscriptions([]);
      return;
    }
    setIsLoadingSubs(true);
    getSubscriptions()
      .then(setSubscriptions)
      .catch(() => setSubscriptions([]))
      .finally(() => setIsLoadingSubs(false));
  }, [user]);

  // Load latest episodes from subscribed podcasts (min 2 per podcast or all from last week)
  useEffect(() => {
    if (subscriptions.length === 0) {
      setLatestFromFavorites([]);
      setIsLoadingFavorites(false);
      return;
    }

    setIsLoadingFavorites(true);
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    
    // Fetch up to 5 episodes from each subscription (limit to first 10 subscriptions)
    const fetchPromises = subscriptions.slice(0, 10).map(async (sub) => {
      try {
        const feed = await getPodcastFeed(sub.feed_url, 5, 0);
        // Filter: take all from last week OR min 2 episodes
        const recentEpisodes = feed.episodes.filter((ep) => {
          const pubDate = ep.published_date ? new Date(ep.published_date).getTime() : 0;
          return pubDate >= oneWeekAgo;
        });
        // Take at least 2 episodes, or all recent ones if more
        const episodesToInclude = recentEpisodes.length >= 2 
          ? recentEpisodes 
          : feed.episodes.slice(0, 2);
        
        return episodesToInclude.map((ep) => ({
          guid: ep.guid,
          title: ep.title,
          description: ep.description,
          audio_url: ep.audio_url,
          published_date: ep.published_date,
          duration: ep.duration,
          episode_number: ep.episode_number,
          podcast_name: sub.podcast_name,
          podcast_artwork_url: sub.artwork_url,
          feed_url: sub.feed_url,
        } as LatestEpisode));
      } catch {
        return [];
      }
    });

    Promise.all(fetchPromises)
      .then((results) => {
        const episodes = results
          .flat()
          .sort((a, b) => {
            const dateA = a.published_date ? new Date(a.published_date).getTime() : 0;
            const dateB = b.published_date ? new Date(b.published_date).getTime() : 0;
            return dateB - dateA;
          });
        setLatestFromFavorites(episodes);
      })
      .catch(() => setLatestFromFavorites([]))
      .finally(() => setIsLoadingFavorites(false));
  }, [subscriptions]);

  // Load popular podcasts + latest episodes on mount
  useEffect(() => {
    getPopularPodcasts()
      .then(setPopularPodcasts)
      .catch(() => setPopularPodcasts([]))
      .finally(() => setIsLoadingPopular(false));

    getLatestEpisodes(12)
      .then(setLatestEpisodes)
      .catch(() => setLatestEpisodes([]))
      .finally(() => setIsLoadingLatest(false));
  }, []);

  // Check which episodes are already transcribed (logged-in users only)
  useEffect(() => {
    if (!user) {
      setTranscribedUrls(new Set());
      return;
    }
    
    // Combine all episode audio URLs
    const allUrls = [
      ...latestFromFavorites.map(ep => ep.audio_url),
      ...latestEpisodes.map(ep => ep.audio_url),
    ].filter(Boolean);
    
    if (allUrls.length === 0) return;
    
    checkTranscribed(allUrls)
      .then((transcribed) => {
        setTranscribedUrls(new Set(Object.keys(transcribed)));
      })
      .catch(() => setTranscribedUrls(new Set()));
  }, [user, latestFromFavorites, latestEpisodes]);

  const handleTranscribe = useCallback(async (episode: LatestEpisode) => {
    if (!episode.audio_url || transcribingGuids.has(episode.guid)) return;
    
    setTranscribingGuids((prev) => new Set(prev).add(episode.guid));
    try {
      const response = await transcribeEpisode(episode.audio_url, {
        title: episode.title,
        podcast_name: episode.podcast_name || '',
        description: episode.description || '',
        artwork_url: episode.podcast_artwork_url || '',
      }, 'auto');
      // Track in global context (shows in bottom bar). Cache hits return
      // status:'completed' immediately — call addCompletedJob to surface the
      // completion banner without entering the polling loop.
      if (response.status === 'completed') {
        addCompletedJob(response.job_id, episode.title, response.result?.saved_item_id);
      } else {
        addJob(response.job_id, episode.title);
      }
    } catch (err) {
      console.error('Transcription failed:', err);
      setTranscribingGuids((prev) => {
        const next = new Set(prev);
        next.delete(episode.guid);
        return next;
      });
    }
  }, [addJob, addCompletedJob, transcribingGuids]);

  const handleSearch = useCallback(async (query: string) => {
    setActiveQuery(query);
    setSearchError(null);

    if (!query) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchPodcasts(query);
      setSearchResults(results);
    } catch (err) {
      setSearchError(
        err instanceof Error ? err.message : "Search failed. Please try again.",
      );
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const showSearchResults = activeQuery.length >= 2;

  return (
    <div
      className="mx-auto max-w-6xl px-4 py-8 sm:px-6"
      style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
    >
      {/* Quick guide banner */}
      <div className="mb-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xl">
            🎧
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Discover & Transcribe Podcasts</h2>
            <p className="mt-1 text-sm text-gray-600">
              Search for your favorite shows, browse trending podcasts, or check out the latest episodes below. 
              Click any episode to get a full transcript with speaker identification in minutes.
            </p>
          </div>
        </div>
      </div>

      <PodcastSearchBar onSearch={handleSearch} isSearching={isSearching} />

      {/* Search error */}
      {searchError && (
        <div className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {searchError}
          <button
            onClick={() => handleSearch(activeQuery)}
            className="ml-2 font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Search results */}
      {showSearchResults && (
        <div className="mt-6">
          <h2 className="mb-3 text-sm font-semibold text-gray-700">
            Search Results
            {!isSearching && searchResults.length > 0 && (
              <span className="ml-1 font-normal text-gray-400">
                ({searchResults.length})
              </span>
            )}
          </h2>
          {isSearching ? (
            <PodcastGrid podcasts={[]} isLoading />
          ) : searchResults.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              No podcasts found for &ldquo;{activeQuery}&rdquo;. Try a different search.
            </p>
          ) : (
            <PodcastGrid podcasts={searchResults} />
          )}
        </div>
      )}

      {/* ── Default view (not searching) ── */}
      {!showSearchResults && (
        <>
          {/* 1. Your Subscriptions — only show if logged in and has subscriptions */}
          {user && !isLoadingSubs && subscriptions.length > 0 && (
            <div className="mt-8">
              <SubscriptionsSection
                subscriptions={subscriptions}
                isLoading={false}
              />
            </div>
          )}

          {/* Show loading state for subscriptions while fetching */}
          {user && isLoadingSubs && (
            <div className="mt-8">
              <SubscriptionsSection
                subscriptions={[]}
                isLoading={true}
              />
            </div>
          )}

          {/* 2. Latest from Favorites — only show if logged in with subscriptions AND there is content (or it is still loading) */}
          {user && subscriptions.length > 0 && (isLoadingFavorites || latestFromFavorites.length > 0) && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-semibold text-gray-700">
                Latest from Favorites
              </h2>
              {isLoadingFavorites ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse rounded-lg bg-gray-100 h-32" />
                  ))}
                </div>
              ) : latestFromFavorites.length > 0 ? (
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
                  {latestFromFavorites.map((episode) => (
                    <div key={episode.guid} className="shrink-0 w-36 sm:w-40">
                      <LatestEpisodeCard 
                        episode={episode} 
                        compact 
                        showTranscribe
                        onTranscribe={handleTranscribe}
                        isTranscribing={transcribingGuids.has(episode.guid)}
                        isTranscribed={transcribedUrls.has(episode.audio_url)}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* 3. Popular Podcasts */}
          <div className="mt-8">
            <h2 className="mb-3 text-sm font-semibold text-gray-700">
              Popular Podcasts
            </h2>
            {isLoadingPopular ? (
              <PodcastGrid podcasts={[]} isLoading horizontal />
            ) : popularPodcasts.length > 0 ? (
              <PodcastGrid podcasts={popularPodcasts} horizontal />
            ) : (
              <p className="py-8 text-center text-sm text-gray-400">
                Search for a podcast to get started
              </p>
            )}
          </div>

          {/* 4. Latest Episodes — hidden until loaded, then fades in (with empty-state fallback when loaded-empty) */}
          {!isLoadingLatest && (
            <div className="mt-8 animate-fade-in">
              <h2 className="mb-3 text-sm font-semibold text-gray-700">
                Latest Episodes
              </h2>
              {latestEpisodes.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
                  {latestEpisodes.map((episode) => (
                    <LatestEpisodeCard 
                      key={episode.guid} 
                      episode={episode} 
                      compact 
                      isTranscribed={transcribedUrls.has(episode.audio_url)}
                    />
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-gray-400">
                  No recent episodes available right now.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
